import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { mui } from '~/tools/mui';
import { icons } from '~/tools/icons';
import { jss } from '~/tools/jss';
import { useTranslation, useAppDispatch } from '~/behaviors';
import { accountActions } from '~/behaviors/actions/account.actions';
import Button from '~/components/Button';
import { PasswordInput, Dialogs } from '../UI.shared';

const useStyles = jss.makeStyles((theme) => ({
  cardContent: {
    padding: 24,
    width: 476,
  },
  instructionText: {
    fontSize: 18,
    textAlign: 'center',
  },
  form: {
    textAlign: 'center',
    minHeight: 100,
    marginTop: 24,
  },
  inputLabel: {
    fontSize: 14,
  },
  input: {
    fontSize: 14,
    height: 40,
    boxSizing: 'border-box',
    padding: '8px 16px',
  },
  confirmationPassword: {
    marginTop: 24,
  },
  errorMessage: {
    marginTop: 24,
  },
  submitBtn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    height: 40,
    textTransform: 'capitalize',
    fontSize: 16,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
    '&:disabled': {
      backgroundColor: '#bdbdbd',
      color: 'white',
    },
  },
  submitBtnContainer: {
    margin: '24px 0px 0px 0px',
  },
  errText: {
    paddingTop: theme.spacing(2),
  },
  navLinkContainer: {
    marginTop: 24,
  },
  link: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': {
      color: theme.legacyPalette.darkaccent,
    },
  },
}));

const defaultDialogState = {
  title: '',
  message: '',
  isVisible: false,
  closeButtonLabel: '',
};

const ResetPasswordPage = () => {
  // ------------------------------------------------------------------ State --
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { reset_token: resetToken } = useParams();

  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(defaultDialogState);

  const { register, errors, handleSubmit } = useForm({ mode: 'onChange' });
  const isValidForm = !!newPassword && !errors.newPassword;

  // ----------------------------------------------------------------- Events --
  const handleDialogClose = () => {
    navigate('/login');
    setDialog(defaultDialogState);
  };

  const handleFormSubmit = (formData) => {
    // @ts-ignore @TODO Epic WT-21600
    dispatch(accountActions.resetPassword({ resetToken, newPassword })).then((action) => {
      if (action.error) {
        return setError(action.error.message);
      }

      setDialog({
        title: t('reset.password.dialog.title'),
        message: t('reset.password.dialog.description'),
        isVisible: true,
        closeButtonLabel: t('back.to.login'),
      });
    });
  };

  // -------------------------------------------------------------- Interface --
  return (
    <mui.CardContent className={styles.cardContent} data-testid="reset-password-page">
      <Dialogs.Simple
        title={dialog.title}
        message={dialog.message}
        isVisible={dialog.isVisible}
        closeButtonLabel={dialog.closeButtonLabel}
        onClose={handleDialogClose}
        Icon={icons.LockOpen}
      />

      <mui.Typography className={styles.instructionText}>{t('reset.password.instruction')}</mui.Typography>

      <form className={styles.form} onSubmit={handleSubmit(handleFormSubmit)}>
        <PasswordInput
          size="small"
          toggleVisibility
          checkStrength
          name="newPassword"
          label={t('user.reset.password')}
          variant="outlined"
          error={!!errors.newPassword}
          helperText={t(errors['currentPassword.message'])}
          inputRef={register({
            required: 'form.isRequired',
          })}
          inputProps={{ 'data-testid': 'password' }}
          InputProps={{ classes: { input: styles.input } }}
          InputLabelProps={{ classes: { root: styles.inputLabel } }}
          onChange={(e) => setNewPassword(e.target.value)}
        />

        {error && (
          <mui.FormHelperText error id="error-text" data-testid="error" className={styles.errorMessage}>
            {error}
          </mui.FormHelperText>
        )}

        <div className={styles.submitBtnContainer}>
          <Button
            aria-label={t('user.reset.header')}
            fullWidth
            variant="contained"
            type="submit"
            disabled={!isValidForm}
            className={styles.submitBtn}
            data-testid="submit"
          >
            {t('user.reset.password')}
          </Button>
        </div>

        <div className={styles.navLinkContainer}>
          <Link className={styles.link} to="/login">
            {t('back.to.login')}
          </Link>
        </div>
      </form>
    </mui.CardContent>
  );
};

export default ResetPasswordPage;
